import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { AssignedUser } from '@deltek/specification-client/core/Models/listing'
import { ListingLevel } from '@deltek/specification-client/core/Models/product'
import { User } from '@deltek/specification-client/core/Models/user'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { ProductListingCardLogo } from '@deltek/specification-client/core/Widgets/ProductListing/Card/ProductListingCardLogo'
import ViewBuildingType from '@deltek/specification-client/core/Widgets/ProductListing/Details/GeneralInfo/ViewBuildingType'
import ViewDescription from '@deltek/specification-client/core/Widgets/ProductListing/Details/GeneralInfo/ViewDescription'
import ViewDetail from '@deltek/specification-client/core/Widgets/ProductListing/Details/GeneralInfo/ViewDetail'
import {
    SenderInfo,
    ViewFirmContacts,
} from '@deltek/specification-client/core/Widgets/ProductListing/Details/GeneralInfo/ViewFirmContacts'
import ViewName from '@deltek/specification-client/core/Widgets/ProductListing/Details/GeneralInfo/ViewName'
import ViewPublishedDate from '@deltek/specification-client/core/Widgets/ProductListing/Details/GeneralInfo/ViewPublishedDate'
import { Typography } from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { removeFamilyNumberFromFamilyNameRegex } from 'bpm/pages/ProductsPage/components/common'
import { MONTH_DATE_YEAR_FORMAT } from 'bpm/pages/constants'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { addMinutes, format } from 'date-fns'
import * as React from 'react'
import { useEffect, useMemo } from 'react'
import Truncate from 'react-truncate'
import { EditFirmContacts } from './Contacts/EditFirmContacts'
import { EditBuildingType } from './GeneralInfo/EditBuildingType'
import { EditCategories } from './GeneralInfo/EditCategories'
import { EditContentLibrary } from './GeneralInfo/EditContentLibrary'
import { EditDescriptionRTF } from './GeneralInfo/EditDescriptionRTF'
import { EditModel } from './GeneralInfo/EditModel'
import { EditName } from './GeneralInfo/EditName'
import {
    ExtendedListingDetail,
    ProductListingDetailForm,
    ProductListingDetailFormField,
} from './ListingDetailsPage'
import { ListingDetailProperties } from './ListingDetailsProperties'
import { RoleBasedAcessMatrix } from './ListingDetailsUtils'

const useStyles = makeStyles((theme: Theme) => ({
    generalInfoContainer: {
        padding: theme.spacing(2),
        paddingRight: 0,
        display: 'block',
        color: '#474747',
        [theme.breakpoints.up('md')]: {
            paddingLeft: theme.spacing(2),
        },
        [theme.breakpoints.between('sm', 'md')]: {
            paddingLeft: theme.spacing(0.2),
        },
        userSelect: 'none',
    },
    font: { fontWeight: 500 },
    spacing: {
        marginTop: theme.spacing(1),
        alignItems: 'baseline',
    },
    layoutContainer: {
        overflowY: 'auto',
        width: '100%',
    },
    family: {
        marginLeft: theme.spacing(1),
        fontWeight: 500,
    },
    typographySpacing: {
        marginBottom: theme.spacing(1),
    },
    premiumSpacingLeft: { marginLeft: theme.spacing(0.5) },
    spacingLeft: { marginLeft: theme.spacing(1) },
    spacingRight: { marginRight: theme.spacing(1) },
    spacingTop: { marginTop: '-5px' },
    propertiesContainer: { marginTop: theme.spacing(2) },
    firmLogo: {
        width: 200,
        height: 60,
        objectFit: 'scale-down',
        objectPosition: 'left',
    },
    basicLayout: {
        marginBottom: theme.spacing(2),
    },
    productName: {
        fontSize: pxToRem(36),
        fontWeight: 500,
        lineHeight: 1,
        marginBottom: theme.spacing(3),
        marginTop: theme.spacing(1),
        wordWrap: 'break-word',
    },
    buildingType: {
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(1.75),
    },
    productInfo: { marginLeft: 'auto', marginRight: '45px' },
    spacingLeftMargin: { marginLeft: 'auto' },
    listingLogo: { display: 'flex', alignItems: 'center' },
    proprietaryBlock: {
        fontSize: pxToRem(13),
        color: '#FFFFFF',
        backgroundColor: '#FD3431',
        '& span': {
            margin: '3px',
        },
    },
    ProprietaryContainer: {
        borderRight: '1px solid #000000',
    },
    AGDspacing: { marginTop: theme.spacing(2) },
    asignedContactHdr: {
        //Figma specified styles
        fontWeight: 700,
        color: '#242424',
        lineHeight: '21.09px',
        margin: theme.spacing(1.5, 0, 2),
    },
    noContacts: {
        marginBottom: theme.spacing(2),
        fontSize: pxToRem(15),
        lineHeight: pxToRem(theme.spacing(2.25)),
        color: '#242424',
    },
    container: { paddingTop: theme.spacing(2) },
}))

interface ProductDetailsProps {
    productInfo: Partial<ExtendedListingDetail>
    userId?: string
    editMode: boolean
    showListingInfo?: boolean
    productListingDetailForm: ProductListingDetailForm
    handleFieldChange: (
        field: ProductListingDetailFormField,
        value: string | string[]
    ) => void
    handlePropertiesChange: (
        value: Record<string, Record<string, string[]>>
    ) => void
    roles?: {
        isDeltekUser?: boolean
        isDeltekAdmin?: boolean
        isDeltekPublishingAuthor?: boolean
        isDeltekAuthor?: boolean
        isBPMAdmin?: boolean
        isBPMAuthor?: boolean
        isGuestBPMReseller?: boolean
    }
    isProductDuplicate?: boolean
    newListingRequest?: boolean
    roleBasedAccess?: Partial<RoleBasedAcessMatrix>
    allBPMUsers?: Partial<User>[]
    designatedAccountAdminContact?: Partial<User>[]
    userInfo?: Partial<SenderInfo>
}
export const filterInactiveUsers = (users: Partial<User>[] = []) =>
    users.filter((item) => {
        return item && item.status?.toLowerCase() !== 'inactive'
    })

const shouldShowEditableFields = (
    editMode: boolean,
    roles: ProductDetailsProps['roles']
): boolean =>
    editMode && (!!roles?.isDeltekUser || !!roles?.isGuestBPMReseller)

export const ProductDetails: React.FC<ProductDetailsProps> = ({
    productInfo,
    userId,
    editMode,
    productListingDetailForm,
    handleFieldChange,
    showListingInfo = false,
    handlePropertiesChange,
    roles,
    isProductDuplicate,
    roleBasedAccess = {} as Partial<RoleBasedAcessMatrix>,
    allBPMUsers,
    designatedAccountAdminContact,
    userInfo = {} as Partial<SenderInfo>,
    newListingRequest,
}) => {
    const classes = useStyles()
    const { mFGOnly } = roleBasedAccess

    const [selectedUsers, setSelectedUsers] = React.useState<
        Partial<AssignedUser>[]
    >([])

    const defaultContacts = React.useMemo(() => {
        const activeUsers = filterInactiveUsers(
            productInfo.assignedContactUsers
        )

        return ((activeUsers.length && activeUsers) ||
            designatedAccountAdminContact) as Partial<User>[]
    }, [productInfo.assignedContactUsers, designatedAccountAdminContact])

    const formattedLastUpdated =
        (productInfo.lastUpdated &&
            format(
                addMinutes(
                    new Date(productInfo.lastUpdated),
                    new Date(productInfo.lastUpdated).getTimezoneOffset()
                ),
                MONTH_DATE_YEAR_FORMAT
            )) ||
        ''
    const onContactChangeHandler = (
        _e: React.ChangeEvent<{}>,
        value: Partial<AssignedUser>[]
    ) => {
        setSelectedUsers(value ?? [])
        handleFieldChange?.(
            'assignedContactUsers',
            value && value.length ? JSON.stringify(value) : ''
        )
    }

    const formattedFirstPublished =
        (productInfo.firstPublished &&
            format(
                addMinutes(
                    new Date(productInfo.firstPublished),
                    new Date(productInfo.firstPublished).getTimezoneOffset()
                ),
                MONTH_DATE_YEAR_FORMAT
            )) ||
        ''

    const shouldShowListingInfo = (
        listing: ListingLevel,
        enableBadgelessCardsFlag: string = processEnv.REACT_APP_EnableBadgelessCards
    ): boolean => {
        return (
            enableBadgelessCardsFlag !== '1' ||
            listing === ListingLevel.Manufacturer
        )
    }

    const getListingInfo = (listing: ListingLevel) => {
        let listingInfo: React.ReactNode = shouldShowListingInfo(listing) && (
            <>
                <Typography variant="h6">{productInfo.type}</Typography>
                <Typography className={classes.spacingTop} variant="h6">
                    Listing
                </Typography>
            </>
        )
        if (processEnv.REACT_APP_EnableBadgelessCards !== '1') {
            switch (listing) {
                case ListingLevel.Premium:
                    listingInfo = (
                        <>
                            <Typography variant="h6">Premium</Typography>
                            <ProductListingCardLogo
                                listingLevel={ListingLevel.Premium}
                            />
                        </>
                    )
                    break
                case ListingLevel.PremiumMasterSpec:
                    listingInfo = (
                        <>
                            <Typography
                                className={classes.premiumSpacingLeft}
                                variant="h6"
                            >
                                Premium
                            </Typography>
                            <ProductListingCardLogo
                                listingLevel={ListingLevel.PremiumMasterSpec}
                            />
                        </>
                    )
                    break
                case ListingLevel.PremiumProprietary:
                    listingInfo = (
                        <GridContainer className={clsx(classes.listingLogo)}>
                            <GridItem className={classes.ProprietaryContainer}>
                                <Typography
                                    variant="h6"
                                    className={classes.spacingRight}
                                >
                                    Premium
                                </Typography>
                                <Typography
                                    className={clsx(
                                        classes.proprietaryBlock,
                                        classes.spacingRight,
                                        classes.spacingTop
                                    )}
                                >
                                    <span>Proprietary</span>
                                </Typography>
                            </GridItem>
                            <GridItem
                                className={clsx(
                                    classes.spacingLeft,
                                    classes.AGDspacing
                                )}
                            >
                                {' '}
                                <ProductListingCardLogo
                                    listingLevel={
                                        ListingLevel.PremiumProprietary
                                    }
                                />
                            </GridItem>
                        </GridContainer>
                    )
                    break
                default:
                    break
            }
        }
        return listingInfo
    }

    const filteredUsers = useMemo(() => {
        return allBPMUsers?.filter(
            (e) => !selectedUsers.map((e2) => e2.id).includes(e.id)
        )
    }, [selectedUsers, allBPMUsers])

    const isAuthorizedAdmin = useMemo(
        () => !!roles?.isDeltekAdmin || !!roles?.isBPMAdmin,
        [roles]
    )
    const isAuthorizedAuthor = useMemo(
        () =>
            !!roles?.isDeltekAuthor ||
            !!roles?.isDeltekPublishingAuthor ||
            !!roles?.isBPMAuthor,
        [roles]
    )
    const isBPMLimitedEdit = useMemo(
        () =>
            editMode &&
            roleBasedAccess?.updateBPMLimited,
        [editMode, roleBasedAccess]
    )
    const isContentLibraryEditable = useMemo(
        () =>
            editMode &&
            (!!roles?.isDeltekAuthor ||
                !!roles?.isDeltekPublishingAuthor ||
                !!roles?.isDeltekAdmin),
        [roles, editMode]
    )
    const isContentLibraryVisible = useMemo(
        () =>
            !!Number(processEnv.REACT_APP_ShowContentLibraryOptions) &&
            productInfo?.type === ListingLevel.Manufacturer,
        [productInfo]
    )
    const isCategoryFieldEditable = useMemo(
        () => editMode && (isAuthorizedAdmin || isAuthorizedAuthor || isBPMLimitedEdit),
        [editMode, isAuthorizedAdmin, isAuthorizedAuthor, isBPMLimitedEdit]
    )
    const isFirmContactFieldEditable = useMemo(
        () => editMode && (isAuthorizedAdmin || isAuthorizedAuthor || isBPMLimitedEdit),
        [editMode, isAuthorizedAdmin, isAuthorizedAuthor, isBPMLimitedEdit]
    )

    const productTypeTooltip = useMemo(
        () =>
            roles?.isDeltekUser
                ? `${productInfo.productType} - ${productInfo.specAgentProductTypeId}`
                : '',
        [roles, productInfo]
    )

    useEffect(() => {
        setSelectedUsers(defaultContacts)
    }, [defaultContacts, editMode])

    return (
        <GridContainer
            className={classes.generalInfoContainer}
            data-testid="productDetails"
        >
            <GridItem className={classes.listingLogo}>
                {productInfo.firmLogoUrl ? (
                    <HoverTooltip
                        text={productInfo.firmName}
                        children={
                            <img
                                src={productInfo.firmLogoUrl}
                                className={classes.firmLogo}
                                alt={productInfo.firmName}
                            />
                        }
                    />
                ) : (
                    <Truncate lines={2}>{productInfo.firmName}</Truncate>
                )}

                {showListingInfo && (
                    <GridItem
                        className={clsx(
                            productInfo.type !== ListingLevel.PremiumProprietary
                                ? classes.productInfo
                                : classes.spacingLeftMargin,
                            'listingInfoContainer'
                        )}
                    >
                        {getListingInfo(
                            productInfo.type ?? ListingLevel.Manufacturer
                        )}
                    </GridItem>
                )}
            </GridItem>
            {
                <GridItem className={classes.basicLayout}>
                    <GridContainer direction="column">
                        {shouldShowEditableFields(editMode, roles) ? (
                            <>
                                <GridItem>
                                    <EditModel
                                        value={
                                            productListingDetailForm.model.value
                                        }
                                        valid={
                                            productListingDetailForm.model.valid
                                        }
                                        handleFieldChange={handleFieldChange}
                                        required={
                                            productListingDetailForm.model
                                                .required
                                        }
                                    />
                                </GridItem>
                                <GridItem>
                                    <EditName
                                        value={
                                            productListingDetailForm.productName
                                                .value
                                        }
                                        valid={
                                            productListingDetailForm.productName
                                                .valid
                                        }
                                        required={
                                            productListingDetailForm.productName
                                                .required
                                        }
                                        isDuplicate={isProductDuplicate}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </GridItem>
                            </>
                        ) : (
                            !mFGOnly && (
                                <>
                                    <ViewDetail
                                        name="Model #"
                                        value={productInfo.model}
                                        variant="h6"
                                    />
                                    <GridItem xs={12}>
                                        <ViewName
                                            productName={
                                                productInfo.productName ?? ''
                                            }
                                        />
                                    </GridItem>
                                </>
                            )
                        )}
                    </GridContainer>
                    <Divider />
                </GridItem>
            }
            <GridContainer
                item
                direction="column"
                className={classes.layoutContainer}
            >
                <GridContainer item direction="row">
                    <GridItem xs={isContentLibraryVisible ? 6 : 12}>
                        {isCategoryFieldEditable ? (
                            <EditCategories
                                selectedValues={
                                    productListingDetailForm.categories.value ??
                                    []
                                }
                                newListingRequest={newListingRequest}
                                valid={
                                    productListingDetailForm.categories.valid
                                }
                                handleFieldChange={handleFieldChange}
                                sectionName={productInfo?.sectionTitle}
                                sectionNumber={productInfo?.sectionNumber}
                                listingType={productInfo?.type}
                            />
                        ) : (
                            <ViewDetail
                                name="Category"
                                value={productInfo.categories?.join(', ')}
                                variant="h6"
                            />
                        )}
                    </GridItem>
                    {isContentLibraryVisible && (
                        <GridItem xs={6}>
                            {isContentLibraryEditable ? (
                                <EditContentLibrary
                                    handleFieldChange={handleFieldChange}
                                    value={
                                        productListingDetailForm.contentLibrary
                                            .value
                                    }
                                    valid={
                                        productListingDetailForm.contentLibrary
                                            .valid
                                    }
                                    required={
                                        productListingDetailForm.contentLibrary
                                            .required
                                    }
                                />
                            ) : (
                                <ViewDetail
                                    name="Product Family"
                                    value={productInfo.contentLibrary}
                                    variant="h6"
                                />
                            )}
                        </GridItem>
                    )}
                </GridContainer>
                <GridItem>
                    <ViewDetail
                        name="Family Number"
                        value={productInfo.sectionNumber}
                        variant="h6"
                    />
                </GridItem>
                <GridItem>
                    <ViewDetail
                        name="Family"
                        value={productInfo.sectionName
                            ?.replace(removeFamilyNumberFromFamilyNameRegex, '')
                            .toUpperCase()}
                        variant="h6"
                    />
                </GridItem>

                {productInfo?.type !== ListingLevel.Manufacturer && (
                    <GridItem>
                        <Typography
                            variant="h6"
                            className={classes.asignedContactHdr}
                        >
                            Product Contact:
                        </Typography>{' '}
                        {isFirmContactFieldEditable ? (
                            <EditFirmContacts
                                name="product-contacts"
                                items={filteredUsers}
                                selectedItems={selectedUsers}
                                onChange={onContactChangeHandler}
                            />
                        ) : (
                            (!!defaultContacts?.length && (
                                <ViewFirmContacts
                                    contacts={defaultContacts}
                                    productName={productInfo?.productName}
                                    sender={userInfo}
                                    isPublic={true}
                                />
                            )) || (
                                <Typography
                                    variant="body2"
                                    className={classes.noContacts}
                                >
                                    None
                                </Typography>
                            )
                        )}
                    </GridItem>
                )}

                {editMode && !isBPMLimitedEdit ? (
                    <GridItem>
                        <Typography variant="h3">
                            Product Description
                        </Typography>
                        <EditDescriptionRTF
                            value={
                                productListingDetailForm.productDescription
                                    .value
                            }
                            editMode={editMode}
                            valid={
                                productListingDetailForm.productDescription
                                    .valid
                            }
                            required={
                                productListingDetailForm.productDescription
                                    .required
                            }
                            handleFieldChange={handleFieldChange}
                        />
                    </GridItem>
                ) : (
                    !mFGOnly && (
                        <ViewDescription
                            longDescription={productInfo.longDescription}
                        />
                    )
                )}
                {!mFGOnly && (
                    <GridItem className={classes.buildingType}>
                        {editMode ? (
                            <EditBuildingType
                                pastValue={
                                    productListingDetailForm.buildingTypes.value
                                }
                                valid={
                                    productListingDetailForm.buildingTypes.valid
                                }
                                handleFieldChange={handleFieldChange}
                            />
                        ) : (
                            <ViewBuildingType
                                buildingTypes={productInfo.buildingTypes}
                            />
                        )}
                    </GridItem>
                )}
                <Divider />
                <GridContainer item>
                    <GridContainer
                        item
                        lg={3}
                        md={5}
                        xs={6}
                        className={clsx(
                            classes.spacing,
                            classes.typographySpacing
                        )}
                    >
                        {productInfo.firstPublished && (
                            <ViewPublishedDate
                                label="First Publish Date"
                                date={formattedFirstPublished}
                            />
                        )}
                    </GridContainer>
                    <GridContainer
                        item
                        direction="column"
                        lg={3}
                        md={5}
                        xs={6}
                        className={clsx(
                            classes.spacing,
                            classes.font,
                            classes.typographySpacing
                        )}
                    >
                        {productInfo.lastUpdated && (
                            <ViewPublishedDate
                                label="Last Publish Date"
                                date={formattedLastUpdated}
                            />
                        )}
                    </GridContainer>
                </GridContainer>
                <Divider />
                <ViewDetail
                    name="Product Type"
                    value={productInfo.productType}
                    variant="h5"
                    lefSideFontWeight={500}
                    fontSize={14}
                    tooltipText={productTypeTooltip}
                />
                <GridContainer item className={classes.propertiesContainer}>
                    {!mFGOnly && (
                        <ListingDetailProperties
                            level={productInfo?.type ?? ''}
                            userId={userId}
                            editMode={editMode}
                            handlePropertiesChange={handlePropertiesChange}
                            productListingDetailForm={productListingDetailForm}
                            isClearAllOptions={!!roles?.isDeltekAdmin}
                        />
                    )}
                </GridContainer>
            </GridContainer>
        </GridContainer>
    )
}
